import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { Link } from 'react-router-dom';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于 JMComic – 品牌介绍、口碑评价与联系方式',
  description: '了解 JMComic 漫画阅读平台的品牌故事、用户口碑评价与联系方式。',
  url: 'https://drawgates.github.io/about',
  isPartOf: { '@type': 'WebSite', name: 'JMComic', url: 'https://drawgates.github.io' },
  about: {
    '@type': 'Organization',
    name: 'JMComic',
    description: 'JMComic 是深受用户喜爱的漫画阅读平台，提供海量优质漫画资源与流畅阅读体验。',
  },
};

const testimonials = [
  {
    name: '小明',
    role: '漫画爱好者',
    avatar: 'https://readdy.ai/api/search-image?query=A%20friendly%20young%20person%20avatar%20illustration%20in%20flat%20style%2C%20simple%20design%2C%20warm%20colors%2C%20minimalist%2C%20clean%20background&width=80&height=80&seq=avatar-user-1&orientation=squarish',
    content: 'JMComic 是我用过最好用的漫画阅读平台！资源丰富，更新速度快，阅读体验流畅。每天必刷，已经成为我生活的一部分了。',
    rating: 5,
  },
  {
    name: '樱花雨',
    role: '资深二次元',
    avatar: 'https://readdy.ai/api/search-image?query=A%20cute%20anime%20style%20girl%20avatar%20illustration%2C%20flat%20design%2C%20pink%20and%20soft%20colors%2C%20minimalist%2C%20simple%20and%20clean&width=80&height=80&seq=avatar-user-2&orientation=squarish',
    content: '追了这么多年的漫画，JMComic 的资源库是最全的。特别喜欢它的分类系统，找漫画超级方便，推荐给所有漫友！',
    rating: 5,
  },
  {
    name: '夜影',
    role: '悬疑漫画迷',
    avatar: 'https://readdy.ai/api/search-image?query=A%20cool%20minimalist%20young%20person%20avatar%20illustration%20in%20flat%20style%2C%20dark%20hair%2C%20modern%20design%2C%20simple%20colors%2C%20clean%20background&width=80&height=80&seq=avatar-user-3&orientation=squarish',
    content: '跨设备同步功能太赞了！在公司用电脑看，回家用手机继续，完全无缝衔接。离线缓存功能也很实用。',
    rating: 5,
  },
  {
    name: '风之翼',
    role: '热血漫画死忠',
    avatar: 'https://readdy.ai/api/search-image?query=A%20energetic%20young%20person%20avatar%20illustration%20in%20flat%20style%2C%20vibrant%20orange%20tones%2C%20dynamic%20design%2C%20simple%2C%20clean%20background&width=80&height=80&seq=avatar-user-4&orientation=squarish',
    content: 'JMComic 的热血漫画资源太棒了！每周准时更新，还支持评论互动。认识了很多一起追漫的朋友。',
    rating: 5,
  },
];

export default function About() {
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <ScrollToTop />
      <SEO
        title="关于我们 – JMComic 品牌介绍与联系方式 | 漫画阅读平台"
        description="了解 JMComic 漫画阅读平台的品牌故事、发展历程与核心优势。提供联系方式与官方入口导航，帮助用户快速找到并使用 JMComic 平台。"
        keywords="jmcomic关于,jmcomic品牌,jmcomic联系方式,jmcomic官网导航,jmcomic入口"
        canonicalUrl="https://drawgates.github.io/about"
        ogImage="https://readdy.ai/api/search-image?query=A%20warm%20and%20professional%20brand%20introduction%20illustration%20with%20manga%20elements%2C%20team%20collaboration%20concept%2C%20friendly%20and%20approachable%20style%2C%20soft%20warm%20colors%2C%20clean%20and%20modern%20design&width=1200&height=630&seq=jmcomic-og-about&orientation=landscape"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            {/* Brand Introduction */}
            <section className="mb-16">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-[var(--font-heading)] mb-4">
                关于 JMComic
              </h1>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6 max-w-2xl">
                JMComic 是一个深受广大漫画爱好者喜爱的在线漫画阅读平台。我们致力于为用户提供最新、最全、最优质的漫画资源，以及流畅、便捷的阅读体验。
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-4">
                    <i className="ri-lightbulb-line text-lg text-primary-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-foreground-950 mb-2">我们的使命</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    让每一位漫画爱好者都能轻松找到并畅快阅读自己喜欢的漫画作品，打造国内最受欢迎的漫画阅读社区。
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-4">
                    <i className="ri-eye-line text-lg text-accent-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-foreground-950 mb-2">我们的愿景</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    持续优化阅读体验，拓展漫画资源库，成为漫画爱好者首选的在线阅读平台和社区。
                  </p>
                </div>
              </div>
            </section>

            {/* Core Advantages */}
            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
                为什么选择我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-database-2-line', title: '海量资源', desc: '收录超过 10,000 部漫画作品，涵盖热血、恋爱、奇幻、搞笑、悬疑、科幻、治愈等多元题材，持续更新中。' },
                  { icon: 'ri-flashlight-line', title: '极速加载', desc: '采用先进的图片加载技术，确保高清画质的同时保持极速加载速度，让阅读不再等待。' },
                  { icon: 'ri-device-line', title: '多端适配', desc: '完美适配电脑、平板、手机等多种设备，无论在哪都能获得最佳阅读体验。' },
                  { icon: 'ri-shield-check-line', title: '安全可靠', desc: '平台运营稳定，数据加密传输，用户隐私得到充分保护，让你安心阅读。' },
                ].map((adv, idx) => (
                  <div key={idx} className="flex gap-4 bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                      <i className={`${adv.icon} text-lg text-secondary-600`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">{adv.title}</h3>
                      <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{adv.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* User Testimonials */}
            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {testimonials.map((t, idx) => (
                  <div key={idx} className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/60">
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-950">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                      <div className="ml-auto flex items-center gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-xs text-accent-500" />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-background-100 rounded-xl p-6 border border-background-200/60 text-center">
                <div className="text-3xl md:text-4xl font-bold text-accent-500 font-[var(--font-heading)] mb-1">4.8</div>
                <div className="flex items-center justify-center gap-0.5 mb-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-accent-500" />
                  ))}
                </div>
                <p className="text-sm text-foreground-500">来自 500 万+ 用户的综合评分</p>
              </div>
            </section>

            {/* Contact */}
            <section id="contact" className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/60 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                    <i className="ri-mail-line text-xl text-primary-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@jmcomic.com</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/60 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-3">
                    <i className="ri-wechat-line text-xl text-accent-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">官方微信</h3>
                  <p className="text-xs text-foreground-500">搜索关注：JMComic官方</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/60 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-3">
                    <i className="ri-weibo-line text-xl text-secondary-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">官方微博</h3>
                  <p className="text-xs text-foreground-500">@JMComic官方微博</p>
                </div>
              </div>
            </section>

            {/* Quick Links */}
            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
                快速导航
              </h2>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="px-4 py-2 bg-background-100 border border-background-200/60 rounded-full text-sm text-foreground-700 hover:text-primary-500 hover:border-primary-200 transition-colors"
                >
                  返回首页
                </Link>
                <Link
                  to="/category/all"
                  className="px-4 py-2 bg-background-100 border border-background-200/60 rounded-full text-sm text-foreground-700 hover:text-primary-500 hover:border-primary-200 transition-colors"
                >
                  浏览漫画
                </Link>
                <Link
                  to="/article/article-1"
                  className="px-4 py-2 bg-background-100 border border-background-200/60 rounded-full text-sm text-foreground-700 hover:text-primary-500 hover:border-primary-200 transition-colors"
                >
                  新手指南
                </Link>
                <Link
                  to="/article/article-4"
                  className="px-4 py-2 bg-background-100 border border-background-200/60 rounded-full text-sm text-foreground-700 hover:text-primary-500 hover:border-primary-200 transition-colors"
                >
                  常见问题
                </Link>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}